import { useEffect } from 'react';

import SumsubWebSdk from '@sumsub/websdk-react';
import classNames from 'classnames';
import { observer } from 'mobx-react';

import { Loader } from '../../../components/Loader/Loader';
import { useKycInteractionReport } from '../../../hooks/kyc/useKycInteractionReport';
import { EnumKycStatus } from '../../../services/types';
import type { RootStore } from '../../../stores/RootStore';
import { useStores } from '../../../stores/useStores';
import { getSumSubCheckStatus } from '../helpers';
import type { EnhancedMessageData } from '../types';

import styles from './PlayerKycForm.module.scss';

export const PlayerKycForm = observer(function PlayerKycForm(props: {
  onMessage?: (data: EnhancedMessageData) => void;
  onTokenError?: (stores: RootStore, apiError?: unknown) => void;
  classNames?: Partial<Record<'wrapper' | 'loader' | 'form', string>>;
  isMountedButHidden?: boolean;
  noAutoProfileUpdate?: boolean;
}) {
  const stores = useStores();

  const { handleSumSubMessage } = useKycInteractionReport();

  const handleMessage = (message: string, payload: unknown) => {
    handleSumSubMessage(message, payload);
    const checkStatus = getSumSubCheckStatus(message, payload);

    if (!props.noAutoProfileUpdate) {
      switch (checkStatus) {
        case 'success':
          stores.userStore.updateProfile({ kycStatus: EnumKycStatus.completed });
          break;
        case 'reviewCompleted':
          void stores.userStore.fetchProfile();
      }
    }

    if (typeof props.onMessage === 'function') {
      props.onMessage({ checkStatus, message, payload, stores });
    }
  };

  useEffect(() => {
    if (!props.isMountedButHidden) {
      void stores.authStore.fetchPlayerKycAccessToken(props.onTokenError);
    }

    return () => {
      stores.authStore.setKycAccessToken(null);
    };
  }, [stores.authStore, props.isMountedButHidden, props.onTokenError]);

  return (
    <div className={props.classNames?.wrapper}>
      {!props.isMountedButHidden && stores.authStore.kycAccessToken ? (
        <SumsubWebSdk
          accessToken={stores.authStore.kycAccessToken}
          className={classNames(styles.sumSubIframeWrapper, props.classNames?.form)}
          expirationHandler={() => stores.authStore.fetchPlayerKycAccessToken(props.onTokenError)}
          onMessage={handleMessage}
        />
      ) : (
        <div className={classNames(styles.loader, props.classNames?.loader)}>
          <Loader />
        </div>
      )}
    </div>
  );
});
